import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function FrequencyGiftModal({ frequencyHz, isOpen, onClose }) {
  const [fromUser, setFromUser] = useState("");
  const [toUser, setToUser] = useState("");
  const [message, setMessage] = useState("");
  const [giftId, setGiftId] = useState(null);
  const [giftUrl, setGiftUrl] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Load username from localStorage
      const savedUsername = localStorage.getItem("mirrorsUsername") || "";
      setFromUser(savedUsername);
      setMessage(`I'm sending you ${frequencyHz} Hz to lift your spirits! 🎵✨`);
    } else {
      // Reset on close
      setGiftId(null);
      setGiftUrl("");
      setQrCode("");
      setToUser("");
    }
  }, [isOpen, frequencyHz]);

  const createGift = async () => {
    if (!fromUser.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setLoading(true);
    try {
      // Create gift
      const giftRes = await axios.post(`${API}/frequency-gifts`, {
        frequency_hz: frequencyHz,
        from_user: fromUser.trim(),
        to_user: toUser.trim() || null,
        message: message.trim() || null,
        expires_hours: 168 // 7 days
      });

      const newGiftId = giftRes.data.id;
      setGiftId(newGiftId);

      // Generate URLs
      const baseUrl = window.location.origin;
      const webUrl = `${baseUrl}/gift/${newGiftId}`;
      setGiftUrl(webUrl);
      setDeepLink(`mirrors://frequency/${frequencyHz}?gift=${newGiftId}`);

      // Generate QR code
      const qrRes = await axios.get(`${API}/frequency-gifts/${newGiftId}/qrcode`);
      setQrCode(qrRes.data.qr_code);

      // Save username for future use
      localStorage.setItem("mirrorsUsername", fromUser.trim());

      toast.success("Frequency gift created! 🎁");
    } catch (e) {
      console.error(e);
      toast.error("Failed to create gift");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const shareViaWebShare = async () => {
    if (!navigator.share) {
      toast.error("Web Share not supported on this device");
      return;
    }

    try {
      await navigator.share({
        title: `${fromUser} sent you a frequency gift!`,
        text: message || `Receive ${frequencyHz} Hz from ${fromUser}`,
        url: giftUrl
      });
      toast.success("Shared successfully!");
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e);
        toast.error("Failed to share");
      }
    }
  };

  const sendToMirrors = () => {
    // This would integrate with the existing MIRRORS chirp/sync system
    toast.info("Sending via MIRRORS sync... (Feature in development)");
    // TODO: Send gift notification through WebSocket
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#a4ff2d]">
            🎁 Gift {frequencyHz} Hz Frequency
          </DialogTitle>
        </DialogHeader>

        {!giftId ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share this frequency with a friend to lift their spirits using sound!
            </p>

            <div>
              <Label htmlFor="from">Your Name</Label>
              <Input
                id="from"
                placeholder="Enter your name"
                value={fromUser}
                onChange={(e) => setFromUser(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="to">Recipient Name (Optional)</Label>
              <Input
                id="to"
                placeholder="Friend's name (optional)"
                value={toUser}
                onChange={(e) => setToUser(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Add a personal message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>

            <Button onClick={createGift} disabled={loading} className="w-full">
              {loading ? "Creating Gift..." : "Create Gift 🎁"}
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="link" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="link">Link</TabsTrigger>
              <TabsTrigger value="qr">QR Code</TabsTrigger>
              <TabsTrigger value="imessage">iMessage</TabsTrigger>
              <TabsTrigger value="mirrors">MIRRORS</TabsTrigger>
            </TabsList>

            <TabsContent value="link" className="space-y-4">
              <div>
                <Label>Shareable Link</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={giftUrl} readOnly />
                  <Button onClick={() => copyToClipboard(giftUrl, "Link")} variant="outline">
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Works on any messaging app: WhatsApp, SMS, Telegram, Discord, etc.
                </p>
              </div>

              {navigator.share && (
                <Button onClick={shareViaWebShare} className="w-full" variant="secondary">
                  📱 Share via System Dialog
                </Button>
              )}
            </TabsContent>

            <TabsContent value="qr" className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                {qrCode && (
                  <img src={qrCode} alt="QR Code" className="w-64 h-64 border rounded" />
                )}
                <p className="text-sm text-muted-foreground text-center">
                  Scan this QR code to receive the frequency gift
                </p>
                <Button onClick={() => {
                  const link = document.createElement('a');
                  link.href = qrCode;
                  link.download = `mirrors-gift-${frequencyHz}hz.png`;
                  link.click();
                }} variant="outline">
                  📥 Download QR Code
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="imessage" className="space-y-4">
              <div>
                <Label>Deep Link (iOS)</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={deepLink} readOnly />
                  <Button onClick={() => copyToClipboard(deepLink, "Deep link")} variant="outline">
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  For iOS devices with MIRRORS app installed
                </p>
              </div>

              <div className="p-4 bg-black/40 rounded border border-[#a4ff2d]/20">
                <h4 className="font-semibold mb-2">📱 iOS Instructions:</h4>
                <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                  <li>Copy the deep link above</li>
                  <li>Open iMessage and paste the link</li>
                  <li>Recipient taps link → MIRRORS opens automatically</li>
                  <li>Frequency plays instantly</li>
                </ol>
              </div>

              <Button 
                onClick={() => {
                  const smsUrl = `sms:?&body=${encodeURIComponent(`${message}\n\n${giftUrl}`)}`;
                  window.location.href = smsUrl;
                }}
                className="w-full"
                variant="secondary"
              >
                💬 Open in Messages App
              </Button>
            </TabsContent>

            <TabsContent value="mirrors" className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-[#ff1744]/10 to-[#a4ff2d]/10 border border-[#ff1744] rounded">
                <h4 className="font-semibold mb-2 text-[#a4ff2d]">🌐 MIRRORS Sync Network</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Send directly to friends actively "Mirroring" with you through the MIRRORS network
                </p>
                
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    • Real-time delivery to connected users<br/>
                    • Push notifications for instant awareness<br/>
                    • One-tap accept and play<br/>
                    • Track delivery status
                  </p>
                </div>
              </div>

              <Button onClick={sendToMirrors} className="w-full">
                🚀 Send via MIRRORS Network
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Recipient must be online in the MIRRORS app to receive
              </p>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
